import { useState } from "react";
import { Sparkles } from "lucide-react";

import { useNavigate } from "react-router-dom";

function formatFee(fee) {
  if (fee == null) return "—";
  return `₹${Number(fee).toLocaleString("en-IN")}`;
}

function CollegePreviewCard({ college }) {
  const toTitleCase = (value = "") =>
    value
      .toString()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-md hover:shadow-lg hover:border-green-200 transition-all p-5 flex flex-col h-full">
      <div className="inline-flex items-center gap-1.5 self-start bg-green-50 text-green-700 text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full mb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        Safe
      </div>

      <h4 className="font-black text-gray-900 text-sm leading-snug mb-3 line-clamp-2">
        {toTitleCase(college.institute_name)}
      </h4>

      <div className="space-y-2 mt-auto">
        <InfoRow label="Place" value={toTitleCase(college.place)} />

        <InfoRow label="Branch" value={toTitleCase(college.branch_name)} />

        <InfoRow label="Fee" value={`${formatFee(college.tuition_fee)}/yr`} />

        <InfoRow label="Affiliated to" value={college.affiliated_to} />
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 text-xs items-start">
      <span className="text-gray-400 font-medium">{label}</span>

      <span className="text-gray-700 font-semibold leading-snug break-words">
        {value || "—"}
      </span>
    </div>
  );
}

function CollegeModalCard({ college, category }) {
  const toTitleCase = (value = "") =>
    value
      .toString()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const badge =
    category === "safe"
      ? "bg-green-50 text-green-700"
      : category === "target"
        ? "bg-blue-50 text-aiblue"
        : "bg-orange-50 text-aiorange";

  const badgeLabel =
    category === "safe" ? "Safe" : category === "target" ? "Target" : "Reach";

  return (
    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
      <span className={`inline-block text-xs font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full mb-2 ${badge}`}>
        {badgeLabel}
      </span>

      <p className="font-bold text-gray-900 text-sm leading-snug mb-3">
        {toTitleCase(college.institute_name)}
      </p>

      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-gray-400 font-medium text-xs">Place</span>
          <span className="text-gray-700 font-semibold text-xs leading-snug">
            {toTitleCase(college.place) || "—"}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-gray-400 font-medium text-xs">Branch</span>
          <span className="text-gray-700 font-semibold text-xs leading-snug">
            {toTitleCase(college.branch_name) || "—"}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-gray-400 font-medium text-xs">Fee</span>
          <span className="text-gray-700 font-semibold text-xs leading-snug">
            {formatFee(college.tuition_fee)}/yr
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-gray-400 font-medium text-xs">Affiliated to</span>
          <span className="text-gray-700 font-semibold text-xs leading-snug">
            {college.affiliated_to || "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

function AllCollegesModal({ colleges, data, onClose }) {
  const [activeTab, setActiveTab] = useState("safe");

  const tabs = [
    {
      key: "safe",
      label: "Safe",
      items: colleges?.safe ?? [],
      accent: "bg-green-500",
      activeClass: "bg-green-50 text-green-700 border-green-200",
      inactiveClass: "text-gray-500 hover:text-green-700",
      dot: "bg-green-500",
    },
    {
      key: "target",
      label: "Target",
      items: colleges?.target ?? [],
      accent: "bg-blue-500",
      activeClass: "bg-blue-50 text-aiblue border-blue-200",
      inactiveClass: "text-gray-500 hover:text-aiblue",
      dot: "bg-aiblue",
    },
    {
      key: "reach",
      label: "Reach",
      items: colleges?.reach ?? [],
      accent: "bg-orange-500",
      activeClass: "bg-orange-50 text-aiorange border-orange-200",
      inactiveClass: "text-gray-500 hover:text-aiorange",
      dot: "bg-aiorange",
    },
  ];

  const activeTabData = tabs.find((t) => t.key === activeTab);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-black/60 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col relative my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 sm:px-8 py-5 border-b border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-5 right-5 text-gray-400 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <h3 className="font-black text-xl text-gray-900 pr-8">
            All Recommended Colleges
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Rank {data.rank} · {data.caste} · {data.preferredBranch} ·{" "}
            {data.city}
          </p>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                  activeTab === tab.key
                    ? tab.activeClass
                    : "border-transparent " + tab.inactiveClass
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${tab.dot}`} />
                {tab.label}
                <span
                  className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.key ? "bg-white/60" : "bg-gray-100"
                  }`}
                >
                  {tab.items.length}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-6 sm:px-8 py-6 overflow-y-auto flex-1">
          {activeTabData?.items.length > 0 ? (
            <div className="grid gap-3">
              {activeTabData.items.map((college, idx) => (
                <CollegeModalCard
                  key={`${activeTab}-${college.inst_code}-${college.branch_code}-${idx}`}
                  college={college}
                  category={activeTab}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
                <svg
                  className="w-6 h-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <p className="text-gray-600 font-semibold">
                No {activeTabData?.label} colleges found
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Try adjusting your rank, branch, or budget preferences.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReportCard({ data, report, colleges }) {
  const [showAllColleges, setShowAllColleges] = useState(false);
  const navigate = useNavigate();

  const topSafeColleges = colleges?.safe?.slice(0, 3) ?? [];
  const hasColleges =
    colleges &&
    (colleges.safe?.length ||
      colleges.target?.length ||
      colleges.reach?.length);

  const totalColleges =
    (colleges?.safe?.length ?? 0) +
    (colleges?.target?.length ?? 0) +
    (colleges?.reach?.length ?? 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 px-4">
      {showAllColleges && (
        <AllCollegesModal
          colleges={colleges}
          data={data}
          onClose={() => setShowAllColleges(false)}
        />
      )}

      <div className="max-w-6xl mt-20 mx-auto">
        {/* Colleges section — top of report */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            Colleges Matched
          </div>
          <h1 className="text-5xl font-black text-gray-900 mb-1">
            Your Recommended Colleges
          </h1>
          <p className="text-aiblue font-semibold text-xl">
            Top safe picks for{" "}
            <span className="text-aiorange">{data.fullName}</span>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Rank {data.rank} · {data.caste} category · {data.preferredBranch} ·{" "}
            {data.budget}
          </p>
        </div>

        {hasColleges ? (
          <div className="mb-10">
            {topSafeColleges.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
                {topSafeColleges.map((college, idx) => (
                  <CollegePreviewCard
                    key={`safe-preview-${college.inst_code}-${college.branch_code}-${idx}`}
                    college={college}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-8 text-center mb-6">
                <p className="text-gray-600 font-semibold">
                  No safe colleges matched your criteria.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  View all recommendations for target and reach options.
                </p>
              </div>
            )}

            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowAllColleges(true)}
                className="inline-flex items-center gap-2 bg-aiblue hover:bg-aiblue/90 text-white font-semibold py-4 px-10 rounded-full transition-all shadow-lg hover:shadow-xl active:scale-95 cta-pulse"
              >
                View All
                <span className="bg-white/20 text-xs font-bold px-2 py-0.5 rounded-full">
                  {totalColleges}
                </span>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-10 text-center mb-10">
            <p className="text-gray-600 font-semibold">
              No matching colleges found for your criteria.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Our team can help you explore more options — see your career path
              report below.
            </p>
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-aiblue/30 to-transparent" />
          <span className="text-xs font-bold uppercase tracking-widest text-aiblue">
            Your Career Path with AI.Prof
          </span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-aiblue/30 to-transparent" />
        </div>

        {/* ── Headline card ── */}
        <div className="bg-white rounded-3xl shadow-xl border border-blue-100 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-aiblue to-aiblue/80 px-8 py-6">
            <div className="flex items-start gap-4">
              <div className="bg-white/20 rounded-2xl p-3 flex-shrink-0">
                <Sparkles
                  className="w-8 h-8 text-white"
                  fill="currentColor"
                  strokeWidth={1}
                />
              </div>
              <div>
                <p className="text-blue-200 text-sm font-medium uppercase tracking-wider mb-1">
                  Your AI.Prof Path
                </p>
                <h2 className="text-white text-2xl font-black leading-tight">
                  {report.headline}
                </h2>
              </div>
            </div>
          </div>

          {/* Student context + the problem */}
          <div className="px-8 py-6 space-y-4">
            <p className="text-gray-700 leading-relaxed">
              {report.student_context}
            </p>
            <div className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-1">
                The Challenge Ahead
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {report.the_problem}
              </p>
            </div>
          </div>
        </div>

        {/* ── How AI.Prof Helps ── */}
        <div className="bg-gradient-to-br from-aiblue to-aiblue/80 rounded-3xl shadow-xl p-8 mb-6 text-white">
          <h3 className="font-black text-xl mb-5">How AI.Prof Helps You</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {report.how_aiprof_helps.map((item, idx) => (
              <div
                key={idx}
                className="bg-white/10 rounded-2xl p-4 flex flex-col gap-1.5"
              >
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-aiorange flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-sm font-bold text-white leading-snug">
                    {item.benefit}
                  </span>
                </div>
                <p className="text-xs text-blue-100 leading-snug pl-7">
                  {item.why_it_matters}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Proof Points ── */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 mb-6">
          <h3 className="font-black text-gray-900 text-lg mb-5">
            What You Get with AI.Prof
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {report.proof_points.map((point, idx) => (
              <div
                key={idx}
                className="bg-aiblue/5 rounded-2xl p-4 flex flex-col items-center text-center gap-2"
              >
                {/* <span className="text-2xl">{point.icon}</span> */}
                <span className="text-xs font-semibold text-gray-700 leading-snug">
                  {point.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Path Steps (static) ── */}
        <div className="bg-gray-900 rounded-3xl p-8 mb-6 text-white">
          <h3 className="font-black text-xl mb-5">
            Your 4-Year Journey with AI.Prof
          </h3>
          <div className="space-y-3">
            {[
              {
                step: "Year 1",
                action:
                  "Foundation sprint — learn core tools for your branch, build your first real project with Veda's guidance, and set up your GitHub and portfolio profile.",
              },
              {
                step: "Year 2",
                action:
                  "Skill deepening — take on progressively complex projects, start DSA and domain-specific prep, and earn your guaranteed internship by hitting AI.Prof milestones.",
              },
              {
                step: "Year 3",
                action:
                  "Internship & portfolio — secure a summer internship through AI.Prof's 1,800+ hiring partner network with a portfolio already stronger than most final-year students.",
              },
              {
                step: "Year 4",
                action:
                  "Placement sprint — mock interviews with industry mentors, resume and LinkedIn polish, and a verified portfolio that closes the deal with your target companies.",
              },
            ].map((step, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-aiblue flex items-center justify-center text-xs font-black">
                  {idx + 1}
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{step.step}</p>
                  <p className="text-gray-300 text-sm leading-snug mt-0.5">
                    {step.action}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Branch-specific edge ── */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-8 py-6 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-aiblue"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-aiblue mb-1">
                Why AI.Prof for {data.preferredBranch}
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {report.branch_specific_edge}
              </p>
            </div>
          </div>
        </div>

        {/* ── Parent reassurance ── */}
        <div className="bg-green-50 border border-green-100 rounded-3xl px-8 py-6 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-green-700 mb-1">
                A Note for Parents
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {report.parent_reassurance}
              </p>
            </div>
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="bg-white rounded-3xl shadow-xl border border-blue-100 p-8 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-aiblue bg-aiblue/10 px-4 py-2 rounded-full mb-4">
            NEXT STEP
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">
            Join AI.Prof Career Accelerator
          </h3>
          <p className="text-gray-500 mb-4">
            Start your guided journey toward becoming a job-ready engineer.
          </p>
          <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 rounded-2xl px-5 py-3 mb-6">
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            <span className="text-sm font-semibold">{report.cta_message}</span>
          </div>
          <p className="text-sm text-gray-500 mt-0">
            Need help choosing your path? Contact the AI.Prof team at{" "}
            <a
              href="mailto:support@aiprof.com"
              className="font-medium text-aiblue hover:underline"
            >
              support@aiprof.com
            </a>{" "}
            or call{" "}
            <a
              href="tel:08071580580"
              className="font-medium text-aiblue hover:underline"
            >
              08071 580 580
            </a>
            .
          </p>
          <br />
          <button
            onClick={() => navigate("/waitlist?role=student")}
            className="bg-aiblue hover:bg-aiblue/90 text-white font-semibold py-4 px-10 rounded-full transition-all shadow-lg hover:shadow-xl active:scale-95 cta-pulse"
          >
            Apply Now →
          </button>
        </div>
      </div>
    </div>
  );
}
